import React, { useState, useEffect } from 'react'

// Official Digital SAT Math conversion (44 questions total)
const mathConversion: { [key: number]: number } = {
  0: 200, 1: 200, 2: 200, 3: 200, 4: 210, 5: 230, 6: 250, 7: 270, 8: 290, 9: 310,
  10: 330, 11: 350, 12: 360, 13: 380, 14: 390, 15: 410, 16: 420, 17: 440, 18: 450,
  19: 460, 20: 470, 21: 480, 22: 490, 23: 500, 24: 510, 25: 520, 26: 530, 27: 540,
  28: 550, 29: 560, 30: 570, 31: 590, 32: 610, 33: 630, 34: 650, 35: 670, 36: 690,
  37: 710, 38: 730, 39: 750, 40: 760, 41: 770, 42: 780, 43: 790, 44: 800
}

// Official Digital SAT Reading & Writing conversion (54 questions total)
const readingConversion: { [key: number]: number } = {
  0: 200, 1: 200, 2: 200, 3: 200, 4: 210, 5: 220, 6: 230, 7: 250, 8: 260, 9: 280,
  10: 290, 11: 300, 12: 310, 13: 320, 14: 330, 15: 340, 16: 350, 17: 360, 18: 370,
  19: 380, 20: 390, 21: 400, 22: 410, 23: 420, 24: 430, 25: 440, 26: 450, 27: 460,
  28: 470, 29: 480, 30: 490, 31: 500, 32: 510, 33: 520, 34: 530, 35: 540, 36: 550,
  37: 560, 38: 570, 39: 580, 40: 590, 41: 610, 42: 620, 43: 640, 44: 650, 45: 670,
  46: 680, 47: 700, 48: 710, 49: 730, 50: 740, 51: 760, 52: 770, 53: 780, 54: 800
}

export function SectionCalculators() {
  const [activeSection, setActiveSection] = useState<'math' | 'reading'>('math')
  const [rawScore, setRawScore] = useState<string>('')
  const [mathScore, setMathScore] = useState<string>('')
  const [readingScore, setReadingScore] = useState<string>('')

  // Load from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem('sectionCalculatorData')
    if (saved) {
      try {
        const data = JSON.parse(saved)
        setActiveSection(data.activeSection || 'math')
        setMathScore(data.mathScore || '')
        setReadingScore(data.readingScore || '')
        setRawScore(data.activeSection === 'math' ? data.mathScore || '' : data.readingScore || '')
      } catch (e) {
        console.error('Failed to load saved data:', e)
      }
    }
  }, [])

  // Save to localStorage whenever values change
  useEffect(() => {
    const data = { activeSection, mathScore, readingScore }
    localStorage.setItem('sectionCalculatorData', JSON.stringify(data))
  }, [activeSection, mathScore, readingScore])

  // Update section-specific scores when rawScore changes
  useEffect(() => {
    if (activeSection === 'math') {
      setMathScore(rawScore)
    } else {
      setReadingScore(rawScore)
    }
  }, [rawScore, activeSection])

  const conversion = activeSection === 'math' ? mathConversion : readingConversion
  const maxRaw = activeSection === 'math' ? 44 : 54
  const num = parseInt(rawScore)
  const scaledScore = !isNaN(num) && num >= 0 && num <= maxRaw ? conversion[num] : null

  return (
    <section className="bg-white rounded-lg shadow-md p-6 md:p-8" aria-labelledby="section-calc-heading">
      <h2 id="section-calc-heading" className="text-3xl font-bold text-gray-900 mb-6">
        SAT Section Score Calculators
      </h2>
      <p className="text-gray-600 mb-6">
        Use our SAT test score calculator to convert your raw scores to scaled scores for each section.
      </p>

      {/* Section Tabs */}
      <div className="flex border-b border-gray-200 mb-6" role="tablist">
        <button
          role="tab"
          aria-selected={activeSection === 'math'}
          aria-controls="math-panel"
          onClick={() => {
            setActiveSection('math')
            setRawScore(mathScore)
          }}
          className={`px-6 py-3 font-medium transition-colors ${
            activeSection === 'math'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-600 hover:text-blue-600'
          }`}
        >
          SAT Math Score Calculator
        </button>
        <button
          role="tab"
          aria-selected={activeSection === 'reading'}
          aria-controls="reading-panel"
          onClick={() => {
            setActiveSection('reading')
            setRawScore(readingScore)
          }}
          className={`px-6 py-3 font-medium transition-colors ${
            activeSection === 'reading'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-600 hover:text-blue-600'
          }`}
        >
          SAT English/Reading Score Calculator
        </button>
      </div>

      {/* Calculator Panel */}
      <div
        id={`${activeSection}-panel`}
        role="tabpanel"
        aria-labelledby={`${activeSection}-tab`}
        className="space-y-6"
      >
        <div className="max-w-md">
          <label htmlFor="section-raw-score" className="block text-sm font-medium text-gray-700 mb-2">
            Raw Score (Number of Correct Answers)
          </label>
          <input
            id="section-raw-score"
            type="number"
            min="0"
            max={activeSection === 'math' ? 44 : 54}
            value={rawScore}
            onChange={(e) => setRawScore(e.target.value)}
            className="w-full px-4 py-3 text-lg border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder={activeSection === 'math' ? '0-44' : '0-54'}
            aria-describedby="section-score-hint"
          />
          <p id="section-score-hint" className="text-sm text-gray-500 mt-2">
            Enter the number of questions you answered correctly ({activeSection === 'math' ? '0-44' : '0-54'})
          </p>
        </div>

        {/* Result */}
        {scaledScore !== null && (
          <div className="p-6 bg-gradient-to-r from-blue-50 to-green-50 rounded-lg">
            <div className="text-center">
              <div className="text-sm text-gray-600 mb-2">Scaled Score</div>
              <div className="text-5xl font-bold text-blue-600 mb-2">{scaledScore}</div>
              <div className="text-sm text-gray-600">out of 800</div>
            </div>
          </div>
        )}

        {/* Conversion Table */}
        <div className="mt-8">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">
            {activeSection === 'math' ? 'Math' : 'Reading & Writing'} Score Conversion Table
          </h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Raw Score
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Scaled Score
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Raw Score
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Scaled Score
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {Array.from({ length: Math.ceil(maxRaw / 2) }, (_, i) => i * 2).map((raw) => (
                  <tr key={raw} className={raw === num || raw + 1 === num ? 'bg-blue-50' : ''}>
                    <td className="px-4 py-2 text-sm text-gray-900">{raw}</td>
                    <td className="px-4 py-2 text-sm font-medium text-blue-600">
                      {conversion[raw]}
                    </td>
                    {raw + 1 <= maxRaw && (
                      <>
                        <td className="px-4 py-2 text-sm text-gray-900">{raw + 1}</td>
                        <td className="px-4 py-2 text-sm font-medium text-blue-600">
                          {conversion[raw + 1]}
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </section>
  )
}
